import type { FormSession } from "@/state/formSession";

const PAYMENT_SESSION_KEY = "paymentSession";
const PAYMENT_SESSION_MAX_AGE = 60 * 60;
const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";
const isProd = isBrowser && window.location.protocol === "https:";

const getCookieValue = (name: string) => {
  if (!isBrowser) return null;
  return (
    document.cookie
      .split("; ")
      .find(row => row.startsWith(`${name}=`))
      ?.split("=")[1] ?? null
  );
};

const setCookieValue = (name: string, value: string) => {
  if (!isBrowser) return;
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${PAYMENT_SESSION_MAX_AGE.toString()}`,
    "Path=/",
    "SameSite=Lax",
    isProd ? "Secure" : "",
  ]
    .filter(Boolean)
    .join("; ");
};

const clearCookieValue = (name: string) => {
  if (!isBrowser) return;
  document.cookie = `${name}=; Max-Age=0; Path=/`;
};

export const setPaymentSessionStore = (session: FormSession) => {
  if (!isBrowser) return;
  setCookieValue(PAYMENT_SESSION_KEY, JSON.stringify(session));
};

export const readPaymentSessionStore = (): FormSession => {
  if (!isBrowser) return { formId: null, customerId: null };
  const raw = getCookieValue(PAYMENT_SESSION_KEY);
  if (!raw) return { formId: null, customerId: null };

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<FormSession> | null;
    return {
      formId: parsed?.formId ?? null,
      customerId: parsed?.customerId ?? null,
    };
  } catch {
    return { formId: null, customerId: null };
  }
};

export const clearPaymentSessionStore = () => {
  clearCookieValue(PAYMENT_SESSION_KEY);
};

export const readPaymentSessionFromWindowName = (): FormSession => {
  if (typeof window === "undefined") {
    return { formId: null, customerId: null };
  }

  const params = new URLSearchParams(window.location.search);

  try {
    if (window.name) {
      const parsed = JSON.parse(window.name) as FormSession | null;
      return {
        formId: parsed?.formId ?? null,
        customerId: parsed?.customerId ?? null,
      };
    } else {
      return {
        formId: params.get("formId") ?? null,
        customerId: params.get("customerId") ?? null,
      };
    }
  } catch {
    return { formId: null, customerId: null };
  }
};
